import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Zap, Repeat } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const PortfolioPayoffRatio = ({ payoffBreakdown }) => {
  if (!payoffBreakdown) return null;

  const {
    early_payoff_count = 0,
    early_payoff_total = 0,
    early_payoff_pct = 0,
    refi_payoff_count = 0,
    refi_payoff_total = 0,
    refi_payoff_pct = 0,
    total_payoff_deals = 0,
  } = payoffBreakdown;

  const hasData = total_payoff_deals > 0;

  return (
    <Card className="mb-6" data-testid="payoff-ratio-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Zap className="h-4 w-4 text-cyan-600" />
          Early Payoff vs Refinance Ratio
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="text-sm text-gray-500 italic py-4 text-center" data-testid="payoff-ratio-empty">
            No early payoff or refinance payoff transactions recorded yet.
          </div>
        ) : (
          <>
            {/* Proportional bar */}
            <div
              className="flex h-4 w-full overflow-hidden rounded-full bg-gray-100 mb-4"
              data-testid="payoff-ratio-bar"
            >
              {early_payoff_pct > 0 && (
                <div
                  className="bg-cyan-500 transition-all"
                  style={{ width: `${early_payoff_pct}%` }}
                  title={`Early Payoffs: ${early_payoff_pct.toFixed(1)}%`}
                />
              )}
              {refi_payoff_pct > 0 && (
                <div
                  className="bg-indigo-500 transition-all"
                  style={{ width: `${refi_payoff_pct}%` }}
                  title={`Refi Payoffs: ${refi_payoff_pct.toFixed(1)}%`}
                />
              )}
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                className="rounded-lg border border-cyan-200 bg-cyan-50 p-4"
                data-testid="payoff-ratio-early"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-cyan-700" />
                    <span className="text-sm font-medium text-cyan-800">Early Payoffs</span>
                  </div>
                  <span
                    className="text-xs font-semibold text-cyan-700"
                    data-testid="payoff-ratio-early-pct"
                  >
                    {early_payoff_pct.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-3xl font-bold text-cyan-900"
                    data-testid="payoff-ratio-early-count"
                  >
                    {early_payoff_count}
                  </span>
                  <span className="text-xs text-cyan-700">deals</span>
                </div>
                <div className="text-xs text-cyan-700 mt-1">
                  {formatCurrency(early_payoff_total)} total
                </div>
              </div>

              <div
                className="rounded-lg border border-indigo-200 bg-indigo-50 p-4"
                data-testid="payoff-ratio-refi"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-indigo-700" />
                    <span className="text-sm font-medium text-indigo-800">Refinance Payoffs</span>
                  </div>
                  <span
                    className="text-xs font-semibold text-indigo-700"
                    data-testid="payoff-ratio-refi-pct"
                  >
                    {refi_payoff_pct.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-3xl font-bold text-indigo-900"
                    data-testid="payoff-ratio-refi-count"
                  >
                    {refi_payoff_count}
                  </span>
                  <span className="text-xs text-indigo-700">deals</span>
                </div>
                <div className="text-xs text-indigo-700 mt-1">
                  {formatCurrency(refi_payoff_total)} total
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Based on {total_payoff_deals} deal{total_payoff_deals === 1 ? '' : 's'} with at least one payoff transaction.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PortfolioPayoffRatio;
